import * as React from "react";
import { lineData, Dictionary } from "../../../models/models";
import * as _ from "lodash";
import { SCREEN_WIDTH } from "../../../constants";

declare var d3;

export interface LineGraphProps {
  className?: string;
  data: lineData[];
  currentFilter?: string;
  currentSecondFilter?: string;
  currentTab?: string;
  currentSub?: string;
}

export interface LineGraphState {
  yearThreshold?: "above" | "below";
  showThresholdBtns?: boolean;
}

export class LineGraph extends React.Component<LineGraphProps, LineGraphState> {
  private MIDDLE_YEAR: number = 1997;
  private _lineGraphContainer: HTMLDivElement;

  el: HTMLDivElement;
  constructor(p: LineGraphProps) {
    super(p);

    const canUseLower = this.requiresLowerThreshold(p);
    const canUseHigher = this.requiresHigherThreshold(p);

    this.state = {
      yearThreshold: !!canUseLower ? "below" : "above",
      showThresholdBtns: !!canUseLower && !!canUseHigher
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.didResize);
    this.drawGraph(this.props);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.didResize);
  }

  didResize = (): void => {
    this.drawGraph(this.props);
  };

  componentWillReceiveProps(props: LineGraphProps) {
    if (
      (!!props.data &&
        props.data.length > 0 &&
        (!this.props.data || this.props.data.length !== props.data.length)) ||
      props.currentFilter !== this.props.currentFilter ||
      props.currentSecondFilter !== this.props.currentSecondFilter
    ) {
      const canUseLower = this.requiresLowerThreshold(props);
      const canUseHigher = this.requiresHigherThreshold(props);

      this.setState(
        {
          yearThreshold: !!canUseLower ? "below" : "above",
          showThresholdBtns: !!canUseLower && !!canUseHigher
        },
        () => {
          this.drawGraph(this.props);
        }
      );
    }
  }

  requiresLowerThreshold = (props: LineGraphProps): boolean => {
    if (!props.data || props.data.length === 0) {
      return false;
    }

    let filterList = [];
    if (!!props.currentFilter) {
      filterList.push(props.currentFilter);
    }
    if (!!props.currentSecondFilter) {
      filterList.push(props.currentSecondFilter);
    }

    const filtered = props.data.filter(
      d => filterList.indexOf(d.key) > -1 && d.year < this.MIDDLE_YEAR
    );

    return filtered.length > 0;
  };

  requiresHigherThreshold = (props: LineGraphProps): boolean => {
    if (!props.data || props.data.length === 0) {
      return false;
    }

    let filterList = [];
    if (!!props.currentFilter) {
      filterList.push(props.currentFilter);
    }
    if (!!props.currentSecondFilter) {
      filterList.push(props.currentSecondFilter);
    }

    const filtered = props.data.filter(
      d => filterList.indexOf(d.key) > -1 && d.year >= this.MIDDLE_YEAR
    );

    return filtered.length > 0;
  };

  setYearThresholdVal = (val: "above" | "below"): void => {
    this.setState(
      {
        yearThreshold: val
      },
      () => {
        this.drawGraph(this.props);
      }
    );
  };

  drawGraph = (props: LineGraphProps) => {
    if (!props.data || !props.currentFilter) {
      return;
    }

    d3.select("svg").remove();

    var margin = { top: 20, right: 20, bottom: 30, left: 50 },
      //   width = 960 - margin.left - margin.right,
      //   height = 500 - margin.top - margin.bottom;
      width =
        this._lineGraphContainer.getBoundingClientRect().width -
        margin.left -
        margin.right,
      height =
        this._lineGraphContainer.getBoundingClientRect().height -
        margin.top -
        margin.bottom;

    // set the ranges
    var x = d3.scaleLinear().range([0, width]);
    var y = d3.scaleLinear().range([height, 0]);

    // define the line
    var valueline = d3
      .line()
      .x(function(d: lineData) {
        return x(d.year);
      })
      .y(function(d: lineData) {
        return y(d.value);
      });

    /** TEST DATA START **/
    let filterKeys = [];
    let maxValue = 0;

    // Add the relevant filter keys
    if (!!this.props.currentFilter) {
      filterKeys.push(this.props.currentFilter);
    }
    if (!!this.props.currentSecondFilter) {
      filterKeys.push(this.props.currentSecondFilter);
    }

    const usableData = props.data.filter(d => {
      // Check to make sure that the key is correct
      if (filterKeys.indexOf(d.key) === -1) {
        return false;
      }

      // Update the maximum y value across all years (regardless of year filter button)
      if (d.value > maxValue) {
        maxValue = d.value;
      }

      return (
        // filterKeys.indexOf(d.key) > -1 &&
        this.state.yearThreshold === "below"
          ? d.year < this.MIDDLE_YEAR
          : d.year >= this.MIDDLE_YEAR
      );
    });

    // No data, no graph.
    if (usableData.length === 0) {
      return;
    }

    const dataKeys = _.uniq(usableData.map(ud => ud.key));

    // append the svg obgect to the body of the page
    // appends a 'group' element to 'svg'
    // moves the 'group' element to the top left margin
    var svgInit = d3.select("#line-graph-container").append("svg");

    // SVG Linear Gradient
    var svgDefs = svgInit.append("defs");

    // var colorPallette = filterKeys.length > 1
    //   ? ["#1e94dc", "#8535ba"]
    //   : [this.props.data[0].color];

    // Assign colors to the filterKey, rather than using a regular array...
    var colorPallete: Dictionary<string> = {};
    if (filterKeys.length > 1) {
      colorPallete[filterKeys[0]] = "#1e94dc";
      colorPallete[filterKeys[1]] = "#8535ba";
    } else {
      colorPallete[filterKeys[0]] = this.props.data[0].color;
    }

    for (var numColors = 0; numColors < filterKeys.length; numColors++) {
      const fKey = filterKeys[numColors];
      var svgGradient = svgDefs
        .append("linearGradient")
        .attr("id", `linGrad-${fKey}`)
        .attr("x1", "0%")
        .attr("x2", "0%")
        .attr("y1", "0%")
        .attr("y2", "100%");

      svgGradient
        .append("stop")
        .attr("offset", "0%")
        .attr("style", `stop-color:${colorPallete[fKey]};stop-opacity:1`);

      svgGradient
        .append("stop")
        .attr("offset", "100%")
        .attr("style", `stop-color:#EEE;stop-opacity:0`);
    }

    //   .attr("width", width + margin.left + margin.right)
    //   .attr("height", height + margin.top + margin.bottom)

    var svg = svgInit
      .attr("width", "100%")
      .attr("height", "100%")
      //   .attr(
      //     "viewBox",
      //     `0 0 ${width + margin.left + margin.right} ${height +
      //       margin.top +
      //       margin.bottom}`
      //   )
      // .attr("width", width)
      //   .attr("height", height)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // Scale the range of the data - Tells d3 the min / max values for the X and Y axis (known as domains)
    x.domain(
      d3.extent(usableData, function(d: lineData) {
        return d.year;
      })
    );
    y.domain([
      0,
      maxValue
      // d3.max(usableData, function(d: lineData) {
      //   return d.value;
      // })
    ]);

    // Min / Max year
    const minYear = Math.min(...usableData.map(ud => ud.year));
    const maxYear = Math.max(...usableData.map(ud => ud.year));

    svg
      .append("g")
      .attr("class", "x-axis-g")
      .style("font-size", SCREEN_WIDTH.IS_LARGE() ? "12px" : "10px")
      .attr("transform", "translate(0," + height + ")")
      .style("stroke-width", SCREEN_WIDTH.IS_LARGE() ? "2px" : "1px")
      .call(
        d3
          .axisBottom(x)
          .ticks(maxYear - minYear)
          .tickFormat(val => {
            return val.toString();
          })
      );

    var xTicks = d3.selectAll(".x-axis-g .tick line").nodes();
    d3.select(xTicks[0]).attr("y2", "-950");
    d3.select(xTicks[xTicks.length - 1]).attr("y2", "-950");

    // Add the Y Axis
    svg
      .append("g")
      .call(d3.axisLeft(y))
      .attr("class", "y-axis")
      .style("font-size", SCREEN_WIDTH.IS_LARGE() ? "12px" : "10px");

    // Loop over the different keys and draw their lines
    for (var keyIdx = 0; keyIdx < dataKeys.length; keyIdx++) {
      const datKey = dataKeys[keyIdx];
      const dataThisLine = usableData.filter(ud => ud.key === datKey);

      var area = d3
        .area()
        .x(function(d) {
          return x(d.year);
        })
        .y0(height)
        .y1(function(d) {
          return y(d.value);
        });

      svg
        .append("path")
        .datum(dataThisLine)
        .attr("class", "area")
        .attr("d", area)
        .attr("fill", `url(#linGrad-${datKey})`);

      // Add the valueline path.
      svg
        .append("path")
        .data([dataThisLine])
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", colorPallete[datKey])
        .style("stroke-width", SCREEN_WIDTH.IS_LARGE() ? 2 : 1)

        .attr("d", valueline);

      // Add the X Axis
      // svg
      //   .append("g")
      //   .attr("class", "x-axis-g")
      //   .attr("transform", "translate(0," + height + ")")
      //   .style('stroke-width', SCREEN_WIDTH.IS_LARGE() ? '2px' : "1px")
      //   .call(
      //     d3
      //       .axisBottom(x)
      //       .ticks(maxYear - minYear)
      //       .tickFormat(val => {
      //         return val.toString();
      //       })
      //   );

      // var xTicks = d3.selectAll(".x-axis-g .tick line").nodes();
      // d3.select(xTicks[0]).attr("y2", "-950");
      // d3.select(xTicks[xTicks.length - 1]).attr("y2", "-950");

      // // Add the Y Axis
      // svg.append("g").call(d3.axisLeft(y));

      // Add dots
      svg
        .selectAll("line-dot")
        .data(dataThisLine)
        .enter()
        .append("circle")
        .attr("class", "dot-circle")
        .attr("r", 5)
        .attr("fill", `${colorPallete[datKey]}`)
        .attr("cx", function(d) {
          return x(d.year);
        })
        .attr("cy", function(d) {
          return y(d.value);
        });
    }

    // Add tooltip elements
    const tooltips = svg
      .selectAll("rect-tooptips")
      .data(_.range(minYear, maxYear + 1))
      .enter();

    // Add line for each year
    tooltips
      .append("line")
      .attr("id", function(yr) {
        return `line-year-${yr}`;
      })
      .attr("class", "year-dotted-line")
      // INSERT HOVER LISTENERS HERE
      .attr("x1", (d, i) => {
        return x(d);
      })
      .attr("x2", (d, i) => {
        return x(d);
      })
      .attr("y1", 0)
      .attr("y2", height)
      .attr("stroke", "rgba(0,0,0, 1)")
      .attr("opacity", "0")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "5 5");

    // Invisible Area
    tooltips
      .append("rect")
      // INSERT HOVER LISTENERS HERE
      .attr("x", (d, i) => {
        return x(d) - 10;
      })
      .attr("width", 20)
      .attr("y", 0)
      .attr("height", height)
      .attr("fill", "rgba(0,0,0,0)")
      .on("mouseover", year => {
        const tipData = usableData.filter(ud => ud.year === year);
        const middlePoint = Math.floor((maxYear + minYear) / 2);
        const dir = year > middlePoint ? "left" : "right";
        this.drawTooltips(tipData, d3.event.target, dir);
      })
      .on("touchstart", year => {
      })
      .on("mouseleave", year => {
        this.removeTooltips();
      });
  };

  drawTooltips = (
    yearData: any[],
    parentNode: SVGElement,
    direction: "left" | "right"
  ): void => {
    const parentBounds = parentNode.getBoundingClientRect();
    const tipElement = document.createElement("div");

    tipElement.classList.add("tooltip-element");
    tipElement.classList.add(`${direction}-align-tip`);
    tipElement.classList.add("animated");
    tipElement.classList.add("fadeIn");
    tipElement.classList.add("speed-5");

    tipElement.style.position = "absolute";

    // This is how far the user has scrolled down on the page...
    // Add to the tooltip 'top' value to ensure the correct position!
    const windowYOffset = window.pageYOffset || 0;

    // TODO: remove tooltip on scroll :)... Maybe

    if (direction === "left") {
      // change the -100 to a transform: translateX(-100%) :)
      tipElement.style.left = parentBounds.left + "px";
      tipElement.style.transform = "translateX(-100%)";
    } else {
      tipElement.style.left = parentBounds.left + parentBounds.width + "px";
    }

    tipElement.style.top = parentBounds.top + windowYOffset + "px";
    tipElement.style.width = "135px";
    tipElement.style.height = "auto";
    tipElement.style.border = "1px solid";
    tipElement.style.background = "#FFF";

    const ttipCount = document.createElement("h3");
    ttipCount.innerHTML = "patents";
    tipElement.appendChild(ttipCount);

    yearData.forEach(yd => {
      const yearRowElem = document.createElement("span");

      if (yearData.length > 1) {
        const labelElem = document.createElement("h3");
        labelElem.innerText = yd.label + ":";
        labelElem.style.color =
          this.props.currentSecondFilter === yd.key ? "#8535ba" : "#1e94dc";
        yearRowElem.appendChild(labelElem);
      }
      const valueElem = document.createElement("span");
      valueElem.innerText = `${yd.value}`.replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1,"
      );

      yearRowElem.appendChild(valueElem);
      tipElement.appendChild(yearRowElem);
    });

    var dottedLine = document.getElementById(`line-year-${yearData[0].year}`);
    dottedLine.setAttribute("opacity", "1");

    document.body.appendChild(tipElement);
  };

  removeTooltips = (): void => {
    d3.selectAll(".tooltip-element").remove();
    d3.selectAll(".year-dotted-line").attr("opacity", "0");
  };

  render() {
    let { props, state } = this,
      cls = this.props.className || "";

    return (
      <div className={"line-graph below " + cls} ref={e => (this.el = e)}>
        {!!this.state.showThresholdBtns && (
          <div className="year-filters">
            <div
              className={
                this.state.yearThreshold === "below"
                  ? "filter-btn below active-btn"
                  : "filter-btn below"
              }
              onClick={() => {
                this.setYearThresholdVal("below");
              }}
            >
              1977 - 1996
            </div>
            <div
              className={
                this.state.yearThreshold === "above"
                  ? "filter-btn above active-btn"
                  : "filter-btn above"
              }
              onClick={() => {
                this.setYearThresholdVal("above");
              }}
            >
              1997 - 2018
            </div>
          </div>
        )}
        <div
          style={{ width: "100%", height: "55vh" }}
          ref={e => (this._lineGraphContainer = e)}
          id="line-graph-container"
        />
      </div>
    );
  }
}
